import React, { useMemo } from "react";
import { safeUrl } from "../utils/security";

const ALLOWED_TAGS = new Set([
  "H1", "H2", "H3", "H4", "H5", "H6", "P", "DIV", "SPAN", "BR",
  "STRONG", "B", "EM", "I", "U", "S", "A", "UL", "OL", "LI", "BLOCKQUOTE",
  "HR", "TABLE", "THEAD", "TBODY", "TR", "TH", "TD", "PRE", "CODE"
]);

const TAG_CLASS = {
  H1: "font-heading text-5xl font-black leading-tight my-5",
  H2: "font-heading text-4xl font-black leading-tight my-4",
  H3: "font-heading text-3xl font-black leading-tight my-4",
  H4: "font-heading text-2xl font-bold leading-tight my-3",
  H5: "font-heading text-xl font-bold leading-tight my-3",
  H6: "font-heading text-lg font-bold uppercase tracking-wide my-3",
  P: "my-4 whitespace-pre-wrap",
  DIV: "my-4 whitespace-pre-wrap",
  SPAN: "whitespace-pre-wrap",
  UL: "list-disc pl-6 my-4 space-y-2",
  OL: "list-decimal pl-6 my-4 space-y-2",
  LI: "my-1",
  BLOCKQUOTE: "border-l-4 border-brand-green pl-4 italic text-gray-700 my-4",
  TABLE: "w-full border-collapse my-4 text-sm",
  TH: "border border-gray-200 bg-emerald-50 px-3 py-2 text-left font-semibold",
  TD: "border border-gray-200 px-3 py-2",
  PRE: "my-4 rounded-xl bg-gray-900 text-white p-4 overflow-x-auto text-sm whitespace-pre-wrap",
  CODE: "rounded bg-gray-100 px-1 py-0.5 text-sm",
};

const ALLOWED_TEXT_ALIGN = new Set(["left", "right", "center", "justify"]);
const ALLOWED_TEXT_DECORATION = new Set(["underline", "line-through", "none"]);
const ALLOWED_FONT_STYLE = new Set(["italic", "normal"]);
const ALLOWED_FONT_WEIGHT = new Set(["bold", "normal", "500", "600", "700", "800", "900"]);

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function looksLikeHtml(value = "") {
  return /<\/?[a-z][\s\S]*>/i.test(String(value));
}

function plainTextToRichHtml(value = "") {
  const normalized = String(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = normalized.split(/\n{2,}/).map((block) => block.trimEnd()).filter((block) => block.trim());

  if (blocks.length === 0) return "";

  return blocks
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br />")}</p>`)
    .join("");
}

function normalizeStyleValue(value = "") {
  return String(value).trim().toLowerCase();
}

function isSafeCssColor(value = "") {
  const v = normalizeStyleValue(value);
  return (
    /^#[0-9a-f]{3,8}$/i.test(v) ||
    /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(v) ||
    /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/i.test(v)
  );
}

function getSafeInlineStyle(node) {
  const style = node.getAttribute?.("style") || "";
  if (!style) return "";

  const safe = [];

  style.split(";").forEach((rule) => {
    const [rawProp, ...rawValueParts] = rule.split(":");
    if (!rawProp || rawValueParts.length === 0) return;

    const prop = rawProp.trim().toLowerCase();
    const value = rawValueParts.join(":").trim();
    const normalized = normalizeStyleValue(value);

    if (prop === "text-align" && ALLOWED_TEXT_ALIGN.has(normalized)) {
      safe.push(`text-align: ${normalized}`);
    }

    if (prop === "font-weight" && ALLOWED_FONT_WEIGHT.has(normalized)) {
      safe.push(`font-weight: ${normalized}`);
    }

    if (prop === "font-style" && ALLOWED_FONT_STYLE.has(normalized)) {
      safe.push(`font-style: ${normalized}`);
    }

    if (prop === "text-decoration" && ALLOWED_TEXT_DECORATION.has(normalized)) {
      safe.push(`text-decoration: ${normalized}`);
    }

    if ((prop === "color" || prop === "background-color") && isSafeCssColor(value)) {
      safe.push(`${prop}: ${value}`);
    }
  });

  return safe.join("; ");
}

function sanitizeNode(node, documentRef) {
  if (node.nodeType === Node.TEXT_NODE) {
    return documentRef.createTextNode(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return documentRef.createTextNode("");
  }

  const tagName = node.tagName.toUpperCase();

  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = documentRef.createDocumentFragment();
    Array.from(node.childNodes).forEach((child) => fragment.appendChild(sanitizeNode(child, documentRef)));
    return fragment;
  }

  const clean = documentRef.createElement(tagName.toLowerCase());

  if (TAG_CLASS[tagName]) {
    clean.setAttribute("class", TAG_CLASS[tagName]);
  }

  const safeStyle = getSafeInlineStyle(node);
  if (safeStyle) {
    clean.setAttribute("style", safeStyle);
  }

  if (tagName === "A") {
    const href = safeUrl(node.getAttribute("href") || "", "#");
    clean.setAttribute("href", href);
    clean.setAttribute("rel", "noopener noreferrer");
    if (!href.startsWith("/")) clean.setAttribute("target", "_blank");
    clean.setAttribute("class", "text-brand-green underline hover:text-brand-orange");
  }

  if (tagName === "TH" || tagName === "TD") {
    const colspan = node.getAttribute("colspan");
    const rowspan = node.getAttribute("rowspan");
    if (/^[1-9][0-9]?$/.test(colspan || "")) clean.setAttribute("colspan", colspan);
    if (/^[1-9][0-9]?$/.test(rowspan || "")) clean.setAttribute("rowspan", rowspan);
  }

  Array.from(node.childNodes).forEach((child) => clean.appendChild(sanitizeNode(child, documentRef)));
  return clean;
}

export function sanitizeRichText(html = "") {
  if (!html) return "";

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return escapeHtml(html).replace(/\n/g, "<br />");
  }

  const source = looksLikeHtml(html) ? String(html) : plainTextToRichHtml(html);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${source}</div>`, "text/html");
  const output = document.implementation.createHTMLDocument("");
  const container = output.createElement("div");

  Array.from(doc.body.firstChild?.childNodes || []).forEach((node) => {
    // Ignore les noeuds de texte vides (sauts de ligne entre deux blocs <p>) :
    // sinon, combinés au white-space:pre-wrap, ils créaient de grandes lignes
    // vides entre les paragraphes à l'affichage.
    if (node.nodeType === Node.TEXT_NODE && !(node.textContent || "").trim()) return;
    container.appendChild(sanitizeNode(node, output));
  });

  return container.innerHTML;
}

export function isRichTextEmpty(html = "") {
  const clean = sanitizeRichText(html);
  if (!clean) return true;
  const tmp = document.createElement("div");
  tmp.innerHTML = clean;
  return !tmp.textContent?.trim() && !tmp.querySelector("hr, table, ul, ol, blockquote");
}

export default function RichTextRenderer({ html, as = "div", className = "" }) {
  const Component = as || "div";
  const safeHtml = useMemo(() => sanitizeRichText(html), [html]);

  if (!safeHtml) return null;

  return (
    <Component
      className={`rich-text-renderer leading-relaxed ${className}`}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}